import React, { useRef, useState } from "react"
import { Alert, Badge, Button, Card, CardBody, Spinner, Table, UncontrolledTooltip } from "reactstrap"
import { RatingStar } from "components/ReviewAudit/PublicRatings"
import { OrderArrows } from "components/Common/SponsorBand/RowControls"
import ChangeHistory from "./ChangeHistory"
import { ConfirmDialog, OperationStatus } from "./FeedbackDialogs"
import FeedbackForm from "./FeedbackForm"
import { SAVED, itemIdOf } from "./catalogOperations"
import useCatalogOperation from "./useCatalogOperation"
import { ThemeIcon } from "./themeIcons"
import { LANGUAGES, REASON_FIELDS, THEME_FIELDS, day, isActive, notReady, themeToCheck, variantText } from "./catalogView"

// The Setup tab (docs/design/tournament-feedback, variant B): one compact strip per theme, in the players' order, opened by
// „Edit” into the whole theme, where its texts, reasons, translations, status and order change through the catalog commands
// (sportya-api docs/tournament-feedback.md, „Editarea catalogului din CP”), each confirmed by its receipt.

// „1–3” and the DS star: CP never writes a rating as a text star.
const OneToThree = () => <>1–3<RatingStar published /><span className="sr-only"> stars</span></>

// A variant of one text: its wording, or why it has none, with its state.
function Variant({ variant, field, limit }) {
  const text = variantText(variant, field)
  if (variant?.status === "pending") {
    const previous = variantText({ content: variant.previous }, field)
    return <span className="feedback-setup__variant text-muted">
      <span className="spinner-border spinner-border-sm text-info" aria-hidden="true" /> Translating…
      {previous && <span className="d-block small">Players see „{previous}”</span>}
    </span>
  }
  if (!text) return variant?.status === "manualRequired" ? <Badge className="badge-soft-danger">Manual translation required</Badge>
    : <span className="text-muted">—</span>
  return <span className="feedback-setup__variant">
    {text}
    {(text.length > limit || variant.status === "tooLong") && <> <Badge className="badge-soft-danger">Too long · {text.length}/{limit}</Badge></>}
    {variant.origin === "manual" && <> <Badge className="badge-soft-secondary">Manual</Badge></>}
    {variant.origin === "default" && <> <Badge className="badge-soft-info">Default</Badge></>}
  </span>
}

// Why an answered item has no „Edit” (sportya-api FEEDBACK_ITEM_ANSWERED, Tudor 2026-10-30): its answers keep the meaning they
// were given, so its texts no longer change.
export const answeredText = kind => `Players already answered this ${kind}, so its texts can no longer change: the answers keep the meaning `
  + `they were given. To word it differently, deactivate it and add a new ${kind}.`

// „Edit” of an item, off once players answered it, with a tooltip that says why and what to do instead.
function EditButton({ item, kind, label, ariaLabel, actions, onEdit }) {
  if (!item.answeredAt) return <Button size="sm" color="light" aria-label={ariaLabel} disabled={actions.busy} onClick={onEdit}>{label}</Button>
  const id = `feedback-edit-${itemIdOf(item).replace(/[^a-zA-Z0-9_-]/g, "-")}`
  return <>
    <span id={id} className="d-inline-block feedback-setup__answered" tabIndex={0}>
      <Button size="sm" color="light" aria-label={ariaLabel} aria-describedby={`${id}-why`} disabled style={{ pointerEvents: "none" }}>{label}</Button>
    </span>
    <span id={`${id}-why`} className="sr-only">{answeredText(kind)}</span>
    <UncontrolledTooltip target={id} placement="top">{answeredText(kind)}</UncontrolledTooltip>
  </>
}

// Where an item would appear once active: its place among the active items around it, counted from 1.
const positionOf = (items, item) => items.filter(entry => isActive(entry) || entry === item).indexOf(item) + 1

// The reorder command: every item of the list in its new order, `item` moved one place up or down. The Organizer is not in the
// list of themes (it stays first) and „Altceva” is not in the catalog (it stays last).
function reorder(list, item, direction, themeCode) {
  const next = [...list], from = list.indexOf(item), to = direction === "up" ? from - 1 : from + 1;
  [next[from], next[to]] = [next[to], next[from]]
  return { action: "reorder", ...(themeCode && { themeCode }), items: next.map(entry => ({ itemId: itemIdOf(entry), revision: entry.revision })) }
}
const ratedThemes = themes => themes.filter(theme => theme.type !== "generalNote")

// „Deactivate”, or „Activate” while there is room and the translations are ready; both open the confirmation.
function StatusButton({ item, blocked, full, actions, target }) {
  if (isActive(item)) return <Button size="sm" color="light" disabled={actions.busy} onClick={() => actions.confirm({ action: "deactivate", ...target })}>Deactivate</Button>
  return <Button size="sm" color="primary" outline disabled={actions.busy || full || !!blocked} onClick={() => actions.confirm({ action: "activate", ...target })}>Activate</Button>
}

function ItemStatus({ item, blocked }) {
  const active = isActive(item)
  const since = active ? day(item.statusChangedAt) : null
  return <>
    <Badge className={active ? "badge-soft-success" : "badge-soft-secondary"}>{active ? "Active" : "Inactive"}</Badge>
    {since && <div className="small text-muted">since {since}</div>}
    {!active && blocked && <div className="small text-danger">{blocked}</div>}
  </>
}

// The whole theme, opened from its strip: its code, kind, variants, reasons label, status and the table of its reasons.
function ThemeBody({ theme, catalog, actions }) {
  const { other, limits } = catalog
  const organizer = theme.type === "generalNote"
  const activeReasons = theme.reasons.filter(isActive).length
  const themesFull = catalog.themes.filter(isActive).length >= limits.activeThemes
  const reasonsFull = activeReasons >= limits.activeReasonsPerTheme
  const themeBlocked = notReady(theme, THEME_FIELDS, limits)
  const rated = ratedThemes(catalog.themes)
  return <div className="feedback-setup__strip-body">
    <div className="feedback-setup__theme-head" role="group" aria-label="Theme">
      <div className="feedback-setup__order">{organizer
        ? <span className="small text-muted"><i className="bx bx-lock-alt mr-1" aria-hidden="true" />First</span>
        : <OrderArrows row={{ name: theme.ro.title }} first={rated.indexOf(theme) === 0} last={rated.indexOf(theme) === rated.length - 1}
          disabled={actions.busy} onShift={(row, direction) => actions.send([reorder(rated, theme, direction)])} />}</div>
      <div className="feedback-setup__theme-title">
        <div><code className="small">{theme.code}</code>{" "}
          <Badge className="badge-soft-info">{organizer ? <>No stars · when the general note is <OneToThree /></> : "Stars 1–5"}</Badge></div>
        <div className="small text-muted">
          {LANGUAGES.map(([language, , code], index) => <React.Fragment key={language}>{index > 0 && " · "}
            <span>{code} <Variant variant={theme.variants?.[language]} field="title" limit={limits.title} /></span></React.Fragment>)}
        </div>
        <div className="small text-muted">Reasons label: „{theme.ro.reasonsLabel}”
          {LANGUAGES.map(([language]) => <React.Fragment key={language}>{" · "}
            <Variant variant={theme.variants?.[language]} field="reasonsLabel" limit={limits.reasonsLabel} /></React.Fragment>)}
        </div>
      </div>
      <div className="feedback-setup__theme-status"><ItemStatus item={theme} blocked={themeBlocked} /></div>
      <div className="feedback-setup__actions">
        <EditButton item={theme} kind="theme" label="Edit theme" actions={actions} onEdit={() => actions.edit({ kind: "theme", item: theme })} />
        <StatusButton item={theme} blocked={themeBlocked} full={themesFull} actions={actions}
          target={{ kind: "theme", item: theme, position: positionOf(catalog.themes, theme) }} />
      </div>
    </div>
    <Table size="sm" className="feedback-setup__reasons mb-2">
      <thead><tr><th className="feedback-setup__order-col">Order</th><th>Reason (Romanian)</th><th>English</th><th>Bulgarian</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
      <tbody>
        {theme.reasons.map((item, index) => {
          const blocked = notReady(item, REASON_FIELDS, limits)
          return <tr key={item.code} className={isActive(item) ? "" : "is-inactive"}>
            <td className="text-nowrap"><OrderArrows row={{ name: item.ro.text }} first={index === 0} last={index === theme.reasons.length - 1}
              disabled={actions.busy} onShift={(row, direction) => actions.send([reorder(theme.reasons, item, direction, theme.code)])} /></td>
            <td>{item.ro.text}<div className="small text-muted"><code>{item.code}</code></div></td>
            {LANGUAGES.map(([language]) => <td key={language}><Variant variant={item.variants?.[language]} field="text" limit={limits.reason} /></td>)}
            <td><ItemStatus item={item} blocked={blocked} /></td>
            <td className="feedback-setup__row-actions">
              <EditButton item={item} kind="reason" label="Edit" ariaLabel={`Edit ${item.ro.text}`} actions={actions}
                onEdit={() => actions.edit({ kind: "reason", theme, item })} />{" "}
              <StatusButton item={item} blocked={blocked} full={reasonsFull} actions={actions}
                target={{ kind: "reason", theme, item, position: positionOf(theme.reasons, item) }} />
            </td>
          </tr>
        })}
        <tr className="feedback-setup__other">
          <td><i className="bx bx-lock-alt text-muted" role="img" aria-label="Always last" /></td>
          <td>{other.texts.ro}</td>
          <td>{other.texts.en}</td><td>{other.texts.bg}</td>
          <td colSpan={2} className="small text-muted">Always last, in every theme. Opens „Ce anume?” (up to {limits.otherText} characters).</td>
        </tr>
      </tbody>
    </Table>
    <div className="feedback-setup__theme-foot">
      <Button size="sm" color="link" className="p-0" disabled={actions.busy} onClick={() => actions.edit({ kind: "reason", theme })}>
        <i className="bx bx-plus" aria-hidden="true" /> Add reason</Button>
      <span className={"small " + (activeReasons >= limits.activeReasonsPerTheme ? "text-warning" : "text-muted")}>
        Active reasons: {activeReasons} of {limits.activeReasonsPerTheme}, plus „{other.texts.ro}”</span>
    </div>
  </div>
}

function ThemeStrip({ theme, catalog, actions, isOpen, onToggle }) {
  const { other, limits } = catalog
  const active = isActive(theme)
  const shown = theme.reasons.filter(isActive)
  const inactive = theme.reasons.length - shown.length
  return <section aria-label={theme.ro.title} className={"feedback-setup__strip" + (active ? "" : " is-inactive") + (isOpen ? " is-open" : "")}>
    <div className="feedback-setup__strip-head">
      <ThemeIcon name={theme.icon} />
      <div className="feedback-setup__strip-main">
        <div><strong>{theme.ro.title}</strong>
          {theme.type === "generalNote" && <span className="small text-muted"> · no stars, only when the general note is <OneToThree /></span>}
          {!active && <Badge className="badge-soft-secondary ml-2">Inactive</Badge>}</div>
        {!isOpen && <div className="feedback-setup__chips">
          <ul aria-label="Active reasons">
            {shown.map(item => <li key={item.code} className="feedback-setup__chip">{item.ro.text}</li>)}
            <li className="feedback-setup__chip is-fixed">{other.texts.ro}</li>
          </ul>
          {inactive > 0 && <span className="small text-muted">+ {inactive} inactive</span>}
        </div>}
      </div>
      {themeToCheck(theme, limits) && <Badge className="badge-soft-warning">To check</Badge>}
      <Button size="sm" color="primary" outline={!isOpen} aria-expanded={isOpen} onClick={onToggle}>
        {isOpen ? "Close" : "Edit"} <i className={"bx " + (isOpen ? "bx-chevron-up" : "bx-chevron-down")} aria-hidden="true" />
      </Button>
    </div>
    {isOpen && <ThemeBody theme={theme} catalog={catalog} actions={actions} />}
  </section>
}

// `onChanged` reads the catalog again after a command; `actorId` is the signed-in SuperAdmin, whom the receipts name.
export default function FeedbackSetup({ read, onRetry, onChanged, actorId }) {
  const [open, setOpen] = useState(null)
  const [dialog, setDialog] = useState(null)
  const [notice, setNotice] = useState(false)
  const [historyRefresh, setHistoryRefresh] = useState(0)
  const changed = useRef(onChanged)
  changed.current = onChanged
  // After every result the catalog is read again (a refusal may mean it changed); the history only after a saved change.
  const operation = useCatalogOperation({ actorId, onSettled: result => {
    changed.current?.()
    if (result.saved) setHistoryRefresh(value => value + 1)
    if (result.status === "completed") { setDialog(null); setNotice(true) }
  } })
  const show = next => {
    if (operation.busy) return
    operation.reset()
    setNotice(false)
    setDialog(next)
  }
  const close = () => { setDialog(null); operation.reset() }
  const send = commands => { if (operation.busy) return; operation.reset(); setNotice(false); operation.start(commands) }
  const actions = { busy: operation.busy, send, confirm: target => show({ type: "confirm", target }), edit: target => show({ type: "form", target }) }
  const catalog = read.data
  const limits = catalog?.limits
  const activeThemes = catalog ? catalog.themes.filter(isActive).length : 0
  const full = catalog && activeThemes >= limits.activeThemes
  return <div className="feedback-setup">
    <Card><CardBody>
      <section aria-label="Themes and reasons">
        <div className="feedback-setup__heading mb-2">
          <h5 className="mb-0">Themes and reasons</h5>
          {catalog && <div className="feedback-setup__heading">
            <span className={full ? "text-warning font-weight-medium" : "text-muted"}>Active themes: {activeThemes} of {limits.activeThemes}</span>
            <Button color="primary" size="sm" disabled={operation.busy} onClick={() => actions.edit({ kind: "theme" })}>
              <i className="bx bx-plus mr-1" aria-hidden="true" />Add theme</Button>
          </div>}
        </div>
        {notice && <Alert color="success" isOpen toggle={() => setNotice(false)}>{SAVED}</Alert>}
        {!dialog && <OperationStatus operation={operation} />}
        {read.loading && <div className="text-center my-4"><Spinner color="primary" /></div>}
        {read.error && <div className="alert alert-danger mb-0" role="alert">The feedback catalog could not be loaded.{" "}
          <Button color="link" className="p-0 align-baseline" onClick={onRetry}>Retry</Button></div>}
        {catalog && <>
          <div className="small text-muted mb-2">
            Players see the active themes in this order, each with its active reasons and „{catalog.other.texts.ro}” last. Open a theme to change
            its texts, reasons, translations or order.
            <details className="mt-1"><summary>Rules and limits</summary>
              The Organizer theme has no stars and appears first, only when the general note is 1–3 stars. At most {limits.activeThemes} active
              themes, Organizer included, and {limits.activeReasonsPerTheme} active reasons per theme. Themes and reasons are deactivated, never
              deleted. Texts are written in Romanian and translated automatically.
            </details>
          </div>
          {full && <p className="small text-warning">{limits.activeThemes} of {limits.activeThemes} themes are active: deactivate a theme before activating another.</p>}
          {catalog.themes.map(theme => <ThemeStrip key={theme.code} theme={theme} catalog={catalog} actions={actions}
            isOpen={open === theme.code} onToggle={() => setOpen(current => current === theme.code ? null : theme.code)} />)}
        </>}
      </section>
    </CardBody></Card>
    <Card><CardBody><ChangeHistory refresh={historyRefresh} themes={catalog?.themes} /></CardBody></Card>
    {dialog?.type === "confirm" && <ConfirmDialog target={dialog.target} featureOn={catalog?.feature.enabled} operation={operation} onClose={close}
      onConfirm={() => send([{ action: dialog.target.action, itemId: itemIdOf(dialog.target.item), revision: dialog.target.item.revision }])} />}
    {dialog?.type === "form" && catalog && <FeedbackForm target={dialog.target} catalog={catalog} operation={operation} onSave={send} onClose={close} />}
  </div>
}
